/*
 * Dynamic sticky columns for the product detail "buy box".
 *
 * On desktop the PDP top area is a two-column flex row:
 *   .product__media-wrapper  (gallery, left)  +  .product__info-wrapper (info, right)
 * The two columns rarely have the same height (image count / description length
 * differ per product). This script measures both columns and pins (position:
 * sticky) whichever one is SHORTER, letting the longer one scroll past it — so
 * the short side stays aligned with the long side instead of leaving a gap.
 *
 * Layering: the theme already ships a pure-CSS sticky (both inner elements get
 * `.product__column-sticky` and rely on flex `align-items:stretch`). That alone
 * cannot dynamically switch which side is pinned. When this script runs it adds
 * `.pg-dyn-sticky` to the grid (-> `align-items:flex-start`), pins the shorter
 * WRAPPER, and neutralises the inner `.product__column-sticky` so the two don't
 * fight. If the script never runs, the pure-CSS sticky stays as a fallback.
 *
 * Pure vanilla JS, no dependencies. Activated by `data-dyn-sticky="true"` on the
 * `.product.grid` element (added in main-product.liquid when the section's
 * `enable_sticky_info` setting is on). Styling lives in section-main-product.css
 * (.pg-dyn-sticky / .pg-sticky-col), gated to >=990px where the layout is 2-col.
 */
(function () {
  'use strict';

  var MIN_WIDTH = 990; // matches the >=990px two-column breakpoint in the CSS
  var THRESHOLD = 40;  // px height difference required before we bother pinning

  var mql = window.matchMedia('(min-width: ' + MIN_WIDTH + 'px)');

  function isVisible(el) {
    return !!el && el.offsetParent !== null && el.getBoundingClientRect().height > 0;
  }

  function setup(grid) {
    if (!grid || grid.__pgStickyInit) return;
    grid.__pgStickyInit = true;

    var raf = null;
    var lastPinned; // currently pinned column element (or null) — avoids redundant DOM writes
    var ro = ('ResizeObserver' in window) ? new ResizeObserver(schedule) : null;

    function getColumns() {
      // media_position:'right' renders a duplicate gallery — pick the visible one.
      var medias = grid.querySelectorAll('.product__media-wrapper');
      var media = null;
      for (var i = 0; i < medias.length; i++) {
        if (isVisible(medias[i])) { media = medias[i]; break; }
      }
      var info = grid.querySelector('.product__info-wrapper');
      return { media: media, info: info };
    }

    function clearPins(cols) {
      if (cols.media) cols.media.classList.remove('pg-sticky-col');
      if (cols.info) cols.info.classList.remove('pg-sticky-col');
    }

    function compute() {
      raf = null;
      var cols = getColumns();
      if (!cols.media || !cols.info) return; // columns not ready yet; the grid ResizeObserver re-fires when they appear/resize

      if (!mql.matches) {
        // Mobile: columns stack full-width — never pin.
        grid.classList.remove('pg-dyn-sticky');
        if (lastPinned !== null) { clearPins(cols); lastPinned = null; }
        return;
      }

      // align-items:flex-start lets each wrapper hug its natural height (so we can
      // measure correctly and the shorter sticky column has room to travel).
      grid.classList.add('pg-dyn-sticky');

      // Reading offsetHeight forces the reflow that applies the class above.
      var mediaH = cols.media.offsetHeight;
      var infoH = cols.info.offsetHeight;
      var shorter = (Math.abs(mediaH - infoH) <= THRESHOLD) ? null
                  : (mediaH < infoH ? cols.media : cols.info);

      // Only touch the DOM when the pinned column actually changes — prevents the
      // constant class add/remove churn (style recalc + the DevTools "flashing").
      if (shorter !== lastPinned) {
        clearPins(cols);
        if (shorter) shorter.classList.add('pg-sticky-col');
        lastPinned = shorter;
      }
    }

    function schedule() {
      if (raf) return;
      raf = window.requestAnimationFrame(compute);
    }

    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
    if (mql.addEventListener) {
      mql.addEventListener('change', schedule);
    } else if (mql.addListener) {
      mql.addListener(schedule); // Safari < 14
    }

    // Observe the GRID itself (not the columns) so any layout change — gallery /
    // images loading, variant swaps, async blocks — re-triggers compute. Observing
    // the columns and re-observing inside compute was the old bug (observe() fires
    // an initial callback each call -> schedule -> compute -> observe -> endless
    // rAF loop). Toggling our sticky classes does NOT change the grid's own height,
    // so observing the grid never self-triggers.
    if (ro) { try { ro.observe(grid); } catch (e) {} }

    // Variant swaps and async blocks also mutate the subtree.
    try {
      new MutationObserver(schedule).observe(grid, { childList: true, subtree: true });
    } catch (e) {}

    schedule();
  }

  function init() {
    var grids = document.querySelectorAll('.product.grid[data-dyn-sticky="true"]');
    Array.prototype.forEach.call(grids, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Sections can be injected late (theme editor); re-scan on load.
  window.addEventListener('load', init);
})();
